import React, { useContext, useEffect, useCallback, useState } from 'react'
import { navigate } from 'gatsby'
import { Card, StyledBody, StyledAction } from 'baseui/card'
import { Button } from 'baseui/button'
import { useStyletron } from 'styletron-react'
import { FormControl } from 'baseui/form-control'
import { Input } from 'baseui/input'
import { styled } from 'styletron-react'

import StoreContext from '~/context/StoreContext'
import theme from '~/config/theme'

const ProductCard = ({ product, type, size, onToggle }) => {
  const [css, theme] = useStyletron()
  const {
    title,
    descriptionHtml,
    images: [firstImage],
  } = product
  const {
    variants,
    variants: [initialVariant],
  } = product
  const [noteTo, setNoteTo] = useState('')
  const [noteFrom, setNoteFrom] = useState('')
  const [variant, setVariant] = useState({ ...initialVariant })
  const {
    setCurrentBox,
    addVariantToCart,
    updateUpsell,
    toggleGiftWrapping,
    updateBoxNote,
    store: { client, checkout, hasGiftWrap, currentBox, upsellProducts },
  } = useContext(StoreContext)
  const productVariant =
    client.product.helpers.variantForOptions(product, variant) || variant
  const [available, setAvailable] = useState(productVariant.availableForSale)
  const [toggled, setToggled] = useState(false)

  const [quantity, setQuantity] = useState(
    upsellProducts[productVariant.shopifyId] || 0
  )

  const checkAvailability = useCallback(
    productId => {
      client.product.fetch(productId).then(fetchedProduct => {
        // This checks the currently selected variant for availability
        const result =
          fetchedProduct &&
          fetchedProduct.variants.filter(
            variant => variant.id === productVariant.shopifyId
          )
        if (result && result.length > 0) {
          setAvailable(result[0].available)
        }
      })
    },
    [client.product, productVariant.shopifyId, variants]
  )

  useEffect(() => {
    checkAvailability(product.shopifyId)
  }, [productVariant, checkAvailability, product.shopifyId])

  const isToggled = () => {
    return (
      product &&
      ((product.handle.match(/gift-wrapping/) && hasGiftWrap) ||
        (product.handle.match(/personalisation/) && currentBox.hasNote) ||
        toggled)
    )
  }

  const handleClick = async () => {
    if (product.handle.match(/build-your-own/)) {
      setCurrentBox(productVariant.shopifyId, {
        images: product.images[0],
        handle: product.handle,
      })
      navigate('/build-your-own')
    } else if (product.handle.match(/gift-wrapping/)) {
      toggleGiftWrapping(productVariant.shopifyId)
    } else if (product.handle.match(/personalisation/)) {
      updateBoxNote(
        currentBox.hasNote
          ? false
          : { shopifyId: productVariant.shopifyId, noteTo, noteFrom }
      )
    } else if (onToggle) {
      onToggle()
      setToggled(!toggled)
    } else {
      addVariantToCart(productVariant.shopifyId, 1)
      navigate('/extra-gifts')
    }
  }

  const btnText = () => {
    return product.handle.match(/build-your-own/)
      ? `Build your own box`
      : 'Add to cart'
  }

  const btnStyle = () => {
    if (type === 'toggle' && isToggled()) {
      return {
        BaseButton: {
          style: {
            width: '100%',
            backgroundColor: 'green',
            ':hover': {
              backgroundColor: 'red',
            },
          },
        },
      }
    } else {
      return {
        BaseButton: {
          style: { width: '100%' },
        },
      }
    }
  }

  const handlePlus = () => {
    setQuantity(quantity + 1)
    updateUpsell(productVariant.shopifyId, quantity + 1)
  }

  const handleMinus = () => {
    setQuantity(quantity - 1)
    updateUpsell(productVariant.shopifyId, quantity - 1)
  }

  const imageProps = {
    srcSet: firstImage.localFile.childImageSharp.fluid.srcSet,
    src: firstImage.localFile.childImageSharp.fluid.src,
  }

  const actionBtn = () => {
    if (type === 'toggle') {
      return isToggled() ? (
        <Button
          onClick={() => handleClick()}
          className={css({
            '&:hover': {
              backgroundColor: 'red',
            },
          })}
          overrides={{
            BaseButton: {
              style: ({ $isSelected }) => ({
                width: '100%',
                backgroundColor: 'green',
                ':hover': {
                  backgroundColor: 'green',
                },
              }),
            },
          }}
        >
          {title}
        </Button>
      ) : (
        <Button
          onClick={() => handleClick()}
          kind="secondary"
          overrides={btnStyle()}
        >
          {btnText()}
        </Button>
      )
    } else if (type === 'upsell') {
      return quantity > 0 ? (
        <Quantity>
          <QuantityMinus
            kind="secondary"
            onClick={() => handleMinus()}
            overrides={btnStyle()}
            disabled={quantity <= 0}
          >
            -
          </QuantityMinus>
          <QuantityTxt>{quantity}</QuantityTxt>
          <QuantityPlus
            kind="secondary"
            onClick={() => handlePlus()}
            overrides={btnStyle()}
          >
            +
          </QuantityPlus>
        </Quantity>
      ) : (
        <Button
          onClick={() => handlePlus()}
          kind="secondary"
          overrides={btnStyle()}
        >
          Add to Cart
        </Button>
      )
    } else {
      return (
        <Button onClick={() => handleClick()} overrides={btnStyle()}>
          {btnText()}
        </Button>
      )
    }
  }

  const borderColor = $theme =>
    quantity ? $theme.colors.contentPositive : $theme.colors.border

  const borderHoverColor = $theme => {
    if (!isToggled || !quantity) {
      return $theme.colors.borderAlt
    } else if (isToggled || quantity) {
      return $theme.colors.contentPositive
    }
  }

  return (
    available && (
      <Card
        headerImage={imageProps}
        title={title}
        overrides={{
          Root: {
            style: ({ $theme }) => {
              return {
                borderTopColor: borderColor($theme),
                borderLeftColor: borderColor($theme),
                borderBottomColor: borderColor($theme),
                borderRightColor: borderColor($theme),
                marginBottom: '1rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'all .2s ease-in',
                ':hover': {
                  borderTopColor: borderHoverColor($theme),
                  borderLeftColor: borderHoverColor($theme),
                  borderBottomColor: borderHoverColor($theme),
                  borderRightColor: borderHoverColor($theme),
                },
              }
            },
          },
          HeaderImage: {
            style: {
              height: '130px',
              margin: '1rem',
            },
          },
        }}
      >
        <StyledBody
          className={css({
            marginBottom: '0',
            '@media screen and (min-width: 600px)': {
              minHeight: size ? 'auto' : '196px',
            },
          })}
        >
          <div dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
          {product.handle.match(/personalisation/) && (
            <>
              <FormControl label={() => 'To:'}>
                <Input
                  name="to"
                  onChange={e => setNoteTo(e.target.value)}
                  placeholder="e.g. Shazza"
                />
              </FormControl>
              <FormControl label={() => 'From:'}>
                <Input
                  name="from"
                  onChange={e => setNoteFrom(e.target.value)}
                  placeholder="e.g. Bazza"
                />
              </FormControl>
            </>
          )}
        </StyledBody>
        <h3>${variant && variant.price}</h3>

        <StyledAction>{actionBtn()}</StyledAction>
      </Card>
    )
  )
}

const QuantityPlus = styled(Button, {
  borderTopLeftRadius: '0',
  borderBottomLeftRadius: '0',
})

const QuantityMinus = styled(Button, {
  borderTopRightRadius: '0',
  borderBottomRightRadius: '0',
  ':hover': {
    background: theme.colors.negative100,
    color: theme.colors.negative500,
  },
})

const QuantityTxt = styled('span', {
  padding: '14px 24px',
  background: theme.colors.positive100,
  color: theme.colors.positive500,
})

const Quantity = styled('span', {
  display: 'flex',
  alignItems: 'center',
})
export default ProductCard
